import { useEffect, useMemo } from 'react'
import { useForm, SubmitHandler, useWatch } from 'react-hook-form'
import { GraphQLError } from 'graphql'

import { ProfileData } from '../../types'
import { SessionUser } from '../types'

import { FETCH_SESSION_SUPPLIERS } from '@/gf/queries/session'

import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import useGqlClient from '../hooks/useGqlClient'
import Phone from '@/gf/modules/Phone'

import ModalForm from '@/gf/components/ModalForm'
import TextField from '@/gf/components/TextField'
import { useUpdateUserProfileMutation } from '../_gen/gql'

const UpdateProfile = ({
  user,
  open,
  onClose,
}: {
  user: SessionUser
  open: boolean
  onClose: () => void
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [spinnerLive, spinner] = useToggle()
  const gqlClient = useGqlClient()
  const [updateUser] = useUpdateUserProfileMutation({
    client: gqlClient,
    refetchQueries: [FETCH_SESSION_SUPPLIERS],
  })

  const initialProfileValues = useMemo(
    () => ({ name: user.name, email: user.email, phoneNumber: user.phoneNumber }),
    [user]
  )
  const profileForm = useForm({
    shouldUnregister: true,
    defaultValues: initialProfileValues,
  })

  const phoneNumber = useWatch({ control: profileForm.control, name: 'phoneNumber' })
  useEffect(() => {
    if (phoneNumber) profileForm.setValue('phoneNumber', Phone.format(phoneNumber))
  }, [phoneNumber])

  const onProfileSubmit: SubmitHandler<ProfileData> = (data: ProfileData) => {
    spinner.on()

    const variables = {
      ...data,
    }

    updateUser({
      variables: {
        email: variables.email,
        name: variables.name,
        phoneNumber: variables.phoneNumber ?? null,
        vendorId: null,
        vendorUserId: null,
      },
    })
      .then(() => {
        msgsMgr.add('Profile updated!', 'positive')
        onClose()
      })
      .catch((e: GraphQLError) => msgsMgr.add(e.message, 'negative'))
      .finally(() => spinner.off())
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="Update your profile by adding a phone number"
      onSubmit={profileForm.handleSubmit(onProfileSubmit)}
      submitButtonDisabled={spinnerLive}
      submitButtonShowSpinner={spinnerLive}
    >
      <div className="space-y-4 pt-4">
        <TextField label="Name" {...profileForm.register('name', { required: false })} />
        <TextField label="Email" {...profileForm.register('email', { required: false })} />
        <TextField
          label="Phone number"
          type="tel"
          {...profileForm.register('phoneNumber', { required: false })}
        />
      </div>
    </ModalForm>
  )
}

export default UpdateProfile
